import { FC } from "preact/compat";
import { useComputed } from "@preact/signals";
import { BudgetPurchaseLine, RelationsKind, useApp } from "~/core";
import { getErrorMessage } from "../../utils";
import { getPurchaseOrderAutofill } from "../autofill";
import LinesStep from "./LinesStep";
import { CreateStepProps, PhaseLines } from "./interfaces";
import useLineSelection from "./useLineSelection";
import { getPurchaseLines, isPurchaseLineUsed } from "./utils";

const Purchases: FC<CreateStepProps> = ({
    budget,
    loading,
    hasNext,
    onClose,
    onNext,
}) => {
    const { api, snack } = useApp();
    const selection = useLineSelection();
    const usedCount = useComputed(
        () =>
            getPurchaseLines(budget).filter((line) =>
                isPurchaseLineUsed(api, budget, line),
            ).length,
    );
    const phases = useComputed(() =>
        budget.phases.value
            .map(
                (phase): PhaseLines<BudgetPurchaseLine> => ({
                    phase,
                    lines: phase.lines.value.filter(
                        (line): line is BudgetPurchaseLine =>
                            line.t === "I" &&
                            !isPurchaseLineUsed(api, budget, line),
                    ),
                }),
            )
            .filter((item) => item.lines.length > 0),
    );

    const handleCreate = async () => {
        const selected = selection.getSelected(phases.peek());

        if (selected.length === 0) {
            onNext();
            return;
        }

        const sup = api.supplier.suppliers.peek()[0];
        const project = api.project.getWithCompany(budget.project);

        if (!sup) {
            snack.error(
                "No supplier",
                "A supplier is required to create a purchase order",
            );
            return;
        }

        loading.value = true;

        try {
            const autofill = project
                ? await getPurchaseOrderAutofill(api, project, sup)
                : {};
            const results = await Promise.allSettled(
                selected.map(async (line) => {
                    const orderId = await api.purchase.create({
                        company: api.companyId.value,
                        kam_company: api.companyId.value,
                        our_ref: api.companyId.value,
                        your_ref: project?.client_manager ?? project?.manager,
                        supplier_id: sup._id,
                        project: { project: budget.project },
                        client_name: project?.client,
                        delivery_address1: "",
                        delivery_address2: "",
                        ...autofill,
                        header: line.data.description || "Purchase order",
                        purchase_price: Number(line.data.price_sale ?? 0n),
                        lines: [
                            {
                                line_type: {
                                    $case: "item" as const,
                                    item: {
                                        description:
                                            line.data.description ?? "",
                                        name: line.data.description ?? "",
                                        quantity: line.data.quantity,
                                        unit_price: line.data.unit_price,
                                        unit: "EA",
                                    },
                                },
                            },
                        ],
                    });

                    await api.relation.create({
                        sourceId: line._id,
                        sourceKind: RelationsKind.RelationsKindBudgetPurchase,
                        targetId: orderId,
                        targetKind: RelationsKind.RelationsKindPurchaseOrder,
                    });
                }),
            );
            const successCount = results.filter(
                (item) => item.status === "fulfilled",
            ).length;
            const failCount = results.length - successCount;

            if (successCount) {
                snack.info(
                    "OK",
                    `Created ${successCount} purchase order(s) from the budget`,
                );
            }

            if (failCount) {
                snack.error(
                    "Failed to create purchase order(s)",
                    `${failCount} purchase order(s) could not be created`,
                );
            }

            onNext();
        } catch (err) {
            const message = getErrorMessage(err);

            snack.error("Failed to create purchase order", message);
        } finally {
            loading.value = false;
        }
    };

    const createLabel =
        phases.value.length === 0
            ? hasNext
                ? "Continue"
                : "Done"
            : hasNext
              ? "Create purchase orders & continue"
              : "Create purchase orders";

    return (
        <LinesStep
            title="Select purchases to create"
            emptyText="This budget has no purchase lines to create purchases from"
            phases={phases.value}
            usedCount={usedCount.value}
            selection={selection}
            loading={loading.value}
            createLabel={createLabel}
            onClose={onClose}
            onCreate={handleCreate}
            renderMeta={(line) => (
                <span className="shrink-0 text-gray-500">
                    {line.data.quantity.toNumber()} x{" "}
                    {line.data.unit_price.toNumber()}
                </span>
            )}
        />
    );
};

export default Purchases;
